/**
 * RISKS-DOCUMENT-MANAGEMENT.ROUTE
 * Interface for managing document unique (versions, exports, settings)
 */
import { useState } from "react"
import PageHeader from "@/components/page-header"
import Card from "@/components/card"
import Button from "@/components/button"
import { faDownload, faEye, faFileAlt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAppSelector } from "@/core/root-reducer"
import NavRisks from "../ui/nav-risks"
import { RISKS_TABS } from "../utils/risks-tabs.config"
import RiskExportModal from "../entities/risk/ui/modals/RiskExportModal"

interface DocumentActionProps {
  icon: any
  label: string
  description: string
  buttonLabel: string
  buttonVariant?: "primary" | "secondary"
  onClick: () => void
}

function DocumentAction({
  icon,
  label,
  description,
  buttonLabel,
  buttonVariant = "secondary",
  onClick
}: DocumentActionProps) {
  const session = useAppSelector((state) => state._session)

  return (
    <Card className="flex flex-col p-6 hover:shadow-lg transition-shadow">
      <div className="flex-1">
        <FontAwesomeIcon
          icon={icon}
          size="2x"
          style={{ color: session?.accountColors?.brandPrimary || "#1a3a5c", marginBottom: 16 }}
        />
        <h3 className="font-semibold text-base mb-2">{label}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex flex-col justify-end mt-6">
        <Button
          icon={icon}
          className={buttonVariant}
          onClick={onClick}
          isWithBorder={buttonVariant === "secondary"}
          isFullWidth>
          {buttonLabel}
        </Button>
      </div>
    </Card>
  )
}

export default function RisksDocumentManagementRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showExportModal, setShowExportModal] = useState(false)

  return (
    <NavRisks tabs={RISKS_TABS}>
      {showExportModal && <RiskExportModal onClose={() => setShowExportModal(false)} />}
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faFileAlt} className="grey-t" />
            {t("Document unique")}
          </div>
        }
        subtitle={t("Consultez, exportez ou partagez votre document")}>
        <div className="rel">
          <img
            className="abs"
            src={require("@/assets/owl.png")}
            alt="owl"
            style={{ width: 130, height: "auto", left: -143, top: -40, transform: "scaleX(-1)" }}
          />
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 gap-6 mb-8 px-4">
        <DocumentAction
          icon={faEye}
          label={t("Voir l'aperçu")}
          description={t("Consultez votre document unique avant de l'exporter")}
          buttonLabel={t("Voir l'aperçu")}
          buttonVariant="primary"
          onClick={() => navigate("/risks/document/preview")}
        />

        <DocumentAction
          icon={faDownload}
          label={t("Exporter le document")}
          description={t("Téléchargez votre document unique au format PDF")}
          buttonLabel={t("Exporter")}
          buttonVariant="secondary"
          onClick={() => setShowExportModal(true)}
        />
      </div>
    </NavRisks>
  )
}
